import { memo, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Box, ButtonBase, Typography, useTheme, type Theme } from "@mui/material";
import { designTokens, fontWeight } from "@/theme";
import { selectionFocusVisibleOutline } from "@/theme/designTokens";
import { getComputedLineHeightPx } from "@/utils/get-computed-line-height-px";

export type ExpandableDescriptionProps = {
  /** Plain or multiline description text. */
  text: string;
  /** Optional stable id for aria-controls; auto-generated when omitted. */
  id?: string;
};

const LINE_HEIGHT = 1.6;
const COLLAPSED_LINE_COUNT = 1;
const COLLAPSE_TRANSITION_MS = 125;

const expandButtonSx = (theme: Theme) => ({
  flexShrink: 0,
  px: 0.25,
  py: 0,
  minWidth: 0,
  minHeight: 24,
  fontWeight: fontWeight.medium,
  fontSize: theme.typography.body2.fontSize,
  lineHeight: LINE_HEIGHT,
  color: theme.palette.primary.main,
  textTransform: "none" as const,
  verticalAlign: "baseline",
  borderRadius: `${designTokens.shape.borderRadius.sm}px`,
  "&:hover": {
    textDecoration: "underline",
  },
  "&:focus-visible": selectionFocusVisibleOutline(theme),
});

const readLessLinkSx = (theme: Theme) => ({
  mt: designTokens.spacing.sm / 8,
  px: 0,
  py: 0,
  minWidth: 0,
  minHeight: 32,
  display: "inline-flex",
  alignSelf: "flex-start",
  fontWeight: fontWeight.medium,
  fontSize: theme.typography.body2.fontSize,
  lineHeight: LINE_HEIGHT,
  color: theme.palette.text.secondary,
  textTransform: "none" as const,
  borderRadius: `${designTokens.shape.borderRadius.sm}px`,
  "&:hover": {
    color: theme.palette.text.primary,
    textDecoration: "underline",
  },
  "&:focus-visible": selectionFocusVisibleOutline(theme),
});

/**
 * Collapsible description with single-line preview and inline "+ More" affordance.
 */
export const ExpandableDescription = memo(function ExpandableDescription({
  text,
  id: idProp,
}: ExpandableDescriptionProps) {
  const theme = useTheme();
  const generatedId = useId();
  const bodyId = idProp ?? `item-description-${generatedId}`;
  const [expanded, setExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const [animatedHeight, setAnimatedHeight] = useState<number | null>(null);

  const clampedRef = useRef<HTMLDivElement>(null);
  const fullMeasureRef = useRef<HTMLParagraphElement>(null);

  const textStyles = {
    m: 0,
    color: theme.palette.text.secondary,
    lineHeight: LINE_HEIGHT,
    wordBreak: "break-word" as const,
    fontSize: theme.typography.body1.fontSize,
  };

  const measure = useCallback(() => {
    const clampedEl = clampedRef.current;
    const fullEl = fullMeasureRef.current;
    if (!clampedEl || !fullEl) return;

    const fullHeight = Math.max(fullEl.scrollHeight, fullEl.getBoundingClientRect().height);
    const collapsedLineHeightPx = getComputedLineHeightPx(fullEl);
    const collapsedMaxHeight =
      collapsedLineHeightPx > 0 ? COLLAPSED_LINE_COUNT * collapsedLineHeightPx : fullHeight;
    const textLine = clampedEl.querySelector("[data-description-text='true']") as HTMLElement | null;
    const overflowsByWidth =
      textLine != null && textLine.scrollWidth > textLine.clientWidth + 1;
    const overflows =
      fullHeight > collapsedMaxHeight + 1 ||
      overflowsByWidth ||
      (fullHeight === 0 && text.split(/\r?\n/).filter(Boolean).length > COLLAPSED_LINE_COUNT);

    setShowToggle(overflows);

    if (fullHeight <= 0) {
      setAnimatedHeight(null);
      return;
    }

    const clampedHeight = Math.min(fullHeight, collapsedMaxHeight);
    setAnimatedHeight(expanded ? fullHeight : clampedHeight);
  }, [expanded, text]);

  useLayoutEffect(() => {
    measure();
  }, [text, expanded, measure]);

  useEffect(() => {
    const targets = [clampedRef.current, fullMeasureRef.current].filter(Boolean) as HTMLElement[];
    if (targets.length === 0) return;
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      measure();
    });
    for (const target of targets) {
      observer.observe(target);
    }
    return () => observer.disconnect();
  }, [measure, text]);

  const handleExpand = useCallback(() => {
    setExpanded(true);
  }, []);

  const handleCollapse = useCallback(() => {
    setExpanded(false);
  }, []);

  if (!text.trim()) return null;

  const collapsedTextSx = {
    ...textStyles,
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  };

  const expandedTextSx = {
    ...textStyles,
    whiteSpace: "pre-wrap" as const,
  };

  return (
    <Box
      component="section"
      aria-label="Item description"
      sx={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "flex-start" }}
    >
      <Box
        aria-hidden
        data-testid="expandable-description-measure"
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          width: "100%",
          visibility: "hidden",
          pointerEvents: "none",
          zIndex: -1,
          height: "auto",
          overflow: "visible",
        }}
      >
        <Typography
          ref={fullMeasureRef}
          component="p"
          data-testid="expandable-description-measure-text"
          sx={{ ...textStyles, whiteSpace: "pre-wrap" }}
        >
          {text}
        </Typography>
      </Box>

      <Box
        id={bodyId}
        data-testid="expandable-description-content"
        ref={clampedRef}
        sx={{
          width: "100%",
          overflow: "hidden",
          maxHeight:
            animatedHeight != null && animatedHeight > 0 ? `${animatedHeight}px` : undefined,
          transition: theme.transitions.create("max-height", {
            duration: expanded ? theme.transitions.duration.short : COLLAPSE_TRANSITION_MS,
            easing: expanded ? theme.transitions.easing.easeInOut : theme.transitions.easing.easeOut,
          }),
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
          },
        }}
      >
        {!expanded ? (
          showToggle ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                minWidth: 0,
                gap: 0.25,
              }}
            >
              <Typography component="p" data-description-text="true" sx={collapsedTextSx}>
                {text}
              </Typography>
              <ButtonBase
                type="button"
                onClick={handleExpand}
                aria-expanded={false}
                aria-controls={bodyId}
                sx={expandButtonSx(theme)}
              >
                + More
              </ButtonBase>
            </Box>
          ) : (
            <Typography component="p" data-description-text="true" sx={{ ...textStyles, whiteSpace: "pre-wrap" }}>
              {text}
            </Typography>
          )
        ) : (
          <Typography component="p" data-description-text="true" sx={expandedTextSx}>
            {text}
          </Typography>
        )}
      </Box>

      {expanded && showToggle ? (
        <ButtonBase
          type="button"
          onClick={handleCollapse}
          aria-expanded
          aria-controls={bodyId}
          data-testid="expandable-description-read-less"
          sx={readLessLinkSx(theme)}
        >
          Read less
        </ButtonBase>
      ) : null}
    </Box>
  );
});
